<template>
  <ts-card>
    <div class="form-header ts-bg-gray-100 ts-border-b ts-border-gray-400 ts-p-4 ts-relative">
      <h4 class="ts-text-sm">{{ $t('logicalPosition.pageTitle') }}</h4>
      <ts-modal-close-button />
    </div>
    <ts-card-wrapper>
      <ts-loading-banner :loading="loadingPreData">
        <form>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('logicalPosition.positionNameKh') }}</label>
                <div>
                  <ts-input
                    v-model="model.position_name_kh"
                    :class="{ 'is-invalid': errors.has('position_name_kh') }"
                    autofocus
                  />
                  <div class="invalid-feedback" v-if="errors.has('position_name_kh')">
                    {{ errors.first('position_name_kh') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('logicalPosition.positionNameEn') }}</label>
                <div>
                  <ts-input
                    v-model="model.position_name_en"
                    :class="{ 'is-invalid': errors.has('position_name_en') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('position_name_en')">
                    {{ errors.first('position_name_en') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('logicalPosition.positionLevel') }}</label>
                <div>
                  <ts-select
                    v-model="model.position_level_id"
                    :options="
                      positionLevels.map(position => ({
                        value: position.position_level_id,
                        text: position.position_level_name_en
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('position_level_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('position_level_id')">
                    {{ errors.first('position_level_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{ $t('logicalPosition.jobTitle') }}</label>
                <div>
                  <ts-select
                    v-model="model.job_title_id"
                    :options="
                      jobTitles.map(jobTitle => ({
                        value: jobTitle.job_title_id,
                        text: jobTitle.job_title_en
                      }))
                    "
                    :class="{ 'is-invalid': errors.has('job_title_id') }"
                  />
                  <div class="invalid-feedback" v-if="errors.has('job_title_id')">
                    {{ errors.first('job_title_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label required">{{
                  $t('logicalPosition.orgChartStructure')
                }}</label>
                <div>
                  <ts-select
                    v-model="model.org_chart_structure_id"
                    :options="
                      orgChartStructures.map(structure => ({
                        value: structure.org_chart_structure_id,
                        text: structure.org_chart_structure
                      }))
                    "
                    :class="{
                      'is-invalid': errors.has('org_chart_structure_id')
                    }"
                    @change="onOrgStructureChange"
                  />
                  <div class="invalid-feedback" v-if="errors.has('org_chart_structure_id')">
                    {{ errors.first('org_chart_structure_id') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <label class="col-form-label control-label">{{ $t('logicalPosition.reportToPosition') }}</label>
                <div>
                  <ts-loading-banner :loading="loadingHigherPositions && !loadingPreData">
                    <ts-select
                      v-model="model.report_to_position_id"
                      :options="
                        reportedToPositions.map(pos => ({
                          value: pos.position_id,
                          text: pos.position_name
                        }))
                      "
                    />
                  </ts-loading-banner>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <div>
                  <ts-checkbox v-model="model.is_head">{{ $t('logicalPosition.isHead') }}</ts-checkbox>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group m-b-10">
                <div>
                  <ts-checkbox v-model="model.is_allow_ot_claim">{{ $t('logicalPosition.allowOT') }}</ts-checkbox>
                </div>
              </div>
            </div>
          </div>
          <div class="row ts-mt-6">
            <div class="col-sm-12 col-lg-12 col-md-12 text-right">
              <ts-button @click.prevent="$emit('close')" :disabled="saving">{{ $t('button.cancel') }}</ts-button>
              <ts-button
                outline
                color="primary"
                class="ml-2"
                :waiting="savingAndNew"
                @click.prevent="saveAndNew"
                v-if="editData === undefined"
                >{{ $t('button.createAndNew') }}</ts-button
              >
              <ts-button color="primary" class="ml-2" :waiting="saving" @click.prevent="save">
                {{ editData ? $t('button.update') : $t('button.create') }}
              </ts-button>
            </div>
          </div>
        </form>
      </ts-loading-banner>
    </ts-card-wrapper>
  </ts-card>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { cloneDeep } from 'lodash'
import { mapState } from 'vuex'

export default {
  name: 'logical-position-form',
  props: ['editData'],
  data() {
    const model = {
      position_name_kh: this.editData ? this.editData.position_name_kh : '',
      position_name_en: this.editData ? this.editData.position_name_en : '',
      position_level_id: this.editData ? this.editData.position_level_id : '',
      job_title_id: this.editData ? this.editData.job_title_id : '',
      org_chart_structure_id: this.editData ? this.editData.org_chart_structure_id : '',
      report_to_position_id: this.editData ? this.editData.report_to_position_id : '',
      is_head: this.editData ? this.editData.is_head : false,
      is_allow_ot_claim: this.editData ? this.editData.is_allow_ot_claim : false
    }

    return {
      newModel: model,
      model: cloneDeep(model),
      errors: new Errors(),
      saving: false,
      savingAndNew: false,
      loadingPreData: false,
      loadingHigherPositions: false
    }
  },
  computed: {
    ...mapState('hr/logicalPosition', ['positionLevels', 'jobTitles', 'orgChartStructures', 'reportedToPositions'])
  },
  created() {
    this.loadingPreData = true

    Promise.all([
      this.$store.dispatch('hr/logicalPosition/fetchPositionLevels'),
      this.$store.dispatch('hr/logicalPosition/fetchJobTitles'),
      this.$store.dispatch('hr/logicalPosition/fetchOrgChartStructures'),
      this.$store.dispatch('hr/logicalPosition/fetchHigherPositions', this.model.org_chart_structure_id)
    ]).finally(() => (this.loadingPreData = false))
  },
  methods: {
    onOrgStructureChange() {
      this.model.report_to_position_id = ''

      if (this.model.org_chart_structure_id === '') return

      this.loadingHigherPositions = true
      this.$store
        .dispatch('hr/logicalPosition/fetchHigherPositions', this.model.org_chart_structure_id)
        .then(() => (this.loadingHigherPositions = false))
    },
    submit() {
      let responseResult

      this.errors = new Errors()

      if (this.editData) {
        responseResult = this.$store.dispatch('hr/logicalPosition/update', {
          id: this.editData.position_id,
          ...this.model
        })
      } else {
        responseResult = this.$store.dispatch('hr/logicalPosition/create', this.model)
      }

      return responseResult
        .then(response => {
          return this.$store.dispatch('hr/logicalPosition/fetch').then(res => {
            this.saving = false
            this.$toasted.success(response.message)
            return Promise.resolve(res)
          })
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)

          return Promise.reject(error)
        })
    },
    save() {
      this.saving = true

      this.submit()
        .then(() => {
          this.$emit('close')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveAndNew() {
      this.savingAndNew = true

      this.submit()
        .then(() => {
          this.model = cloneDeep(this.newModel)
        })
        .finally(() => {
          this.savingAndNew = false
        })
    }
  }
}
</script>
